/**
 * The one deployment switch for the optional products.
 *
 * Each entry is a product this deployment either ships or hides. The code
 * for a hidden product stays in the tree; only its doors close: its feature
 * flags answer off whatever an operator set (`~/server/featureFlag/surfaceFlags.ts`),
 * its pages answer not found (`~/components/WithSurfaceGuard.tsx`), and its
 * menu, command-palette and checklist entries are left out.
 *
 * Like `branding.ts` this is static configuration: no environment variables,
 * no runtime dependencies, importable from the browser bundle and the server
 * alike. See specs/branding/product-surfaces.feature.
 */
export const productSurfaces = {
  /** The in-product assistant (the `release_langy_*` flags). */
  langy: false,
  /** The AI Gateway product (`release_ui_ai_gateway_menu_enabled`). */
  gateway: false,
  /**
   * Governance, the personal "Me" product and coding-agent tracking
   * (`release_ui_ai_governance_enabled`, `release_ui_governance_billed_cost_enabled`).
   */
  governance: false,
  /** Simulations / agent testing (`release_ui_agent_testing_v2_enabled`). */
  agentTesting: false,
  /** Voice agents (`release_voice_agents_enabled`). */
  voiceAgents: false,
  /** Public trace share links (`/share/:id` and the Share action). */
  publicShareLinks: false,
  /** Settings → Integrations (the GitHub app). */
  githubIntegration: false,
  /**
   * Passkeys as a way in: the sign-in and sign-up buttons, the Security
   * page's Passkeys section and the prompts to add one. Off here wins over
   * `PASSKEYS_ENABLED`; stored passkeys are kept and come back when it is
   * turned on (`deploymentOffersPasskeys`).
   */
  passkeys: false,
  /** The Support entry at the bottom of every sidebar. */
  supportMenu: false,
  /**
   * The LLM Ops Test and Build pages (Simulations, Experiments, Annotations,
   * Prompts, Agents, Workflows, Datasets, Automations) in menus, the command
   * palette and the home checklist. `"dev-only"` keeps them in development
   * builds and out of production ones; a boolean decides for both.
   */
  buildAndTestPages: "dev-only" as "dev-only" | boolean,
  /**
   * The settings pages this deployment does not offer its customers yet:
   * Security, License, Secrets, Data retention, Email suppressions,
   * Annotation scores and Topic clustering. Same `"dev-only"` rule as above.
   */
  advancedSettings: "dev-only" as "dev-only" | boolean,
};

export type ProductSurface = keyof typeof productSurfaces;

/**
 * Whether a surface is on for this build. Resolves `"dev-only"` against the
 * build mode where a bundler provides it, and to off anywhere else (the
 * server has no `import.meta.env`, and a production server must hide what a
 * production bundle hides).
 */
export function surfaceEnabled(surface: ProductSurface): boolean {
  const value: "dev-only" | boolean = productSurfaces[surface];
  if (value === "dev-only") return isDevBuild();
  return value;
}

function isDevBuild(): boolean {
  const env = (import.meta as unknown as { env?: { DEV?: boolean } }).env;
  if (env && typeof env.DEV === "boolean") return env.DEV;
  return (
    typeof process !== "undefined" && process.env?.NODE_ENV === "development"
  );
}
